import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  Heading,
  HStack,
  Image,
  Input,
  Link,
  SimpleGrid,
  Spinner,
  Text,
  Tooltip,
  useColorModeValue,
  useNumberInput,
  useToast,
} from "@chakra-ui/react";
import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { IProduct } from "../../services/graphql/Product";
import { PRODUCT } from "../../services/graphql/query/Product";
import { ContextApp } from "../../shared/ContextApp";
import { ModalLogin } from "../ModalLogin/ModalLogin";
import { BsCart4, BsHeart, BsHeartFill } from "react-icons/bs";
import { ADD_PRODUCT } from "../../services/graphql/mutation/LineCart";
import { Consultation } from "../Consultation/Consultation";
import {
  CREATE_FAVORITE,
  DELETE_FAVORITE,
} from "../../services/graphql/mutation/Favorites";
import { MessageSuccess } from "../../shared/MessageSuccess";
import { MessageError } from "../../shared/MessageError";

export interface IProductProps {}
export const Product = (props: IProductProps) => {
  const { user } = useContext(ContextApp);
  const [dataProduct, setDataProduct] = useState<IProduct>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingButton, setLoadingButton] = useState<boolean>(false);
  const toast = useToast();
  const { id } = useParams();
  const [addProduct] = useMutation(ADD_PRODUCT);
  const navigate = useNavigate();
  const [createFavorite] = useMutation(CREATE_FAVORITE);
  const [deleteFavorite] = useMutation(DELETE_FAVORITE);
  const [loadingFavorite, setLoadingFavorite] = useState<boolean>(false);

  const {
    valueAsNumber,
    getInputProps,
    getIncrementButtonProps,
    getDecrementButtonProps,
  } = useNumberInput({
    step: 1,
    defaultValue: inputValue,
    min: 1,
    max: dataProduct?.stock,
    precision: 0,
  });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps({ readOnly: true });

  const { fetchMore } = useQuery(PRODUCT, {
    variables: { filter: { id: Number(id) } },
    onCompleted: (data) => {
      const { product } = data;
      setDataProduct(product);
      setLoading(false);
    },
    onError: (error) => {
      setLoading(false);
    },
  });

  const addToCart = () => {
    if (!user) {
      setIsOpen(true);
    } else {
      setLoadingButton(true);
      addProduct({
        variables: {
          input: {
            product_id: dataProduct?.id,
            quantity: valueAsNumber,
          },
        },
      })
        .then(() => {
          toast({
            duration: 2500,
            position: "top",
            render: () => addToCartSuccess(),
          });
        })
        .catch(() => {
          toast({
            duration: 2500,
            position: "top",
            render: () => addToCartError(),
          });
        })
        .finally(() => {
          setLoadingButton(false);
        });
    }
  };

  const addProductFavorites = (product_id: number) => {
    setLoadingFavorite(true);
    createFavorite({
      variables: {
        product_id,
      },
    })
      .then(() => {
        toast({
          duration: 2500,
          position: "top",
          render: () =>
            MessageSuccess({
              title: "Agregado a favoritos",
              description: "",
            }),
        });
        setLoading(true);
        fetchMore({ variables: { filter: { id: Number(id) } } })
          .then((dataProduct) => {
            const { data } = dataProduct;
            const { product } = data;
            setDataProduct(product);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        toast({
          duration: 2500,
          position: "top",
          render: () =>
            MessageError("No pudimos agregar el producto a favoritos"),
        });
      })
      .finally(() => {
        setLoadingFavorite(false);
      });
  };

  const deleteProductFavorites = (idFavorite: number) => {
    setLoadingFavorite(true);
    deleteFavorite({
      variables: {
        id: idFavorite,
      },
    })
      .then(() => {
        toast({
          duration: 2500,
          position: "top",
          render: () =>
            MessageSuccess({
              title: "Eliminado de favoritos",
              description: "",
            }),
        });
        setLoading(true);
        fetchMore({ variables: { filter: { id: Number(id) } } })
          .then((dataProduct) => {
            const { data } = dataProduct;
            const { product } = data;
            setDataProduct(product);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        toast({
          duration: 2500,
          position: "top",
          render: () =>
            MessageError("No pudimos quitar el producto de favoritos"),
        });
      })
      .finally(() => {
        setLoadingFavorite(false);
      });
  };

  const addToCartSuccess = () => (
    <>
      <Alert
        status="success"
        variant="solid"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        height="200px"
      >
        <AlertIcon boxSize="38px" mr={0} />

        <AlertTitle mt={4}>Agregado exitosamente</AlertTitle>
        <AlertDescription display="block">
          Se agrego el producto a su carrito!
        </AlertDescription>
        <Link className="link-without-underline" href="/cart">
          <Button mt={4} variant="solid" colorScheme={"teal"}>
            Ir al carrito
          </Button>
        </Link>
      </Alert>
    </>
  );

  const addToCartError = () => (
    <>
      <Alert status="error" variant="solid">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>Ups!</AlertTitle>
          <AlertDescription display="block">
            Se produjo un error al agregar el producto al carrito.
          </AlertDescription>
        </Box>
        <CloseButton position="absolute" right="8px" top="8px" />
      </Alert>
    </>
  );

  return (
    <>
      <SimpleGrid minChildWidth="200px">
        <Flex
          direction={"column"}
          justifyContent="space-between"
          p={"24px"}
          borderTopRadius={{ base: "3xl", sm: "none" }}
          borderTopRightRadius={{ base: "3xl", sm: "none" }}
          borderLeftRadius={{ base: "none", sm: "2xl" }}
          bg={useColorModeValue("white", "gray.600")}
        >
          {loading ? (
            <Center>
              <Spinner />
            </Center>
          ) : (
            <>
              <Heading as="h1">{dataProduct?.name}</Heading>
              <Text color="gray.500" isTruncated>
                {dataProduct?.description}
              </Text>
              <Text fontWeight={"bold"} fontSize="3xl">
                ${dataProduct?.price}
              </Text>
              <HStack mb={2} maxW="320px">
                <Button {...inc}>+</Button>
                <Input {...input} />
                <Button {...dec}>-</Button>
              </HStack>
              <Flex alignItems={"center"} justifyContent="space-between">
                <Button
                  isLoading={loadingButton}
                  leftIcon={<BsCart4 />}
                  onClick={addToCart}
                  w={user ? "90%" : "100%"}
                >
                  Agregar al carrito
                </Button>
                {user ? (
                  <>
                    {dataProduct?.favorite.filter(
                      (ele) => ele.product_id === dataProduct.id
                    ).length ? (
                      <>
                        <Tooltip
                          label="Quitar producto de favoritos"
                          hasArrow
                          placement="top"
                        >
                          <div>
                            <BsHeartFill
                              onClick={() => {
                                deleteProductFavorites(
                                  dataProduct.favorite[0].id
                                );
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip
                          label="Agregar producto a favoritos"
                          hasArrow
                          placement="top"
                        >
                          <div>
                            <BsHeart
                              onClick={() => {
                                if (dataProduct) {
                                  addProductFavorites(dataProduct.id);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          </div>
                        </Tooltip>
                      </>
                    )}
                  </>
                ) : undefined}
              </Flex>
            </>
          )}
        </Flex>
        <Box
          borderBottomRadius={{ base: "3xl", sm: "none" }}
          borderRightRadius={{ base: "none", sm: "2xl" }}
          border="1px"
          borderColor={"gray.300"}
        >
          <Center>
            {dataProduct?.url_image ? (
              <Image
                style={{ width: "100%", maxWidth: "400px", height: "auto" }}
                src={dataProduct.url_image}
                alt={dataProduct.name}
              />
            ) : null}
          </Center>
        </Box>
      </SimpleGrid>
      <Consultation />
      <ModalLogin
        onClose={() => {
          setIsOpen(false);
        }}
        isOpen={isOpen}
      />
    </>
  );
};
